import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery, keepPreviousData} from '@github-ui/react-query'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  ServerUnavailableError,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {sendStats} from '@github-ui/stats'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useEffect, useMemo, useState} from 'react'
import type {CheckRunForAnalysis, CIFinding, DedupedCIFinding} from './ci-findings-types'
import {deduplicateCIFindings} from './deduplicate-ci-findings'

type CIFindingsAPIResponse = {
  ciFindings: CIFinding[]
  checkRunId?: number
  checkSuiteId?: number
  pullRequestId?: number
  headSha?: string
  availableHeadShas?: string[]
  checkRunsForAnalysis?: CheckRunForAnalysis[]
}

type CIFindingsData = {
  findings: CIFinding[]
  headSha: string | undefined
  checkRunsForAnalysis: CheckRunForAnalysis[] | undefined
}

export type AnalysisProgress = {
  /** Number of failed check runs that have at least one finding. */
  analyzedCount: number
  /** Total number of failed check runs. */
  failedCount: number
  /** Number of check runs still queued or in progress. */
  pendingCount: number
}

export const POLL_INTERVAL_MS = 10_000
export const MAX_POLL_DURATION_MS = 2 * 60 * 60 * 1000 // 2 hours — CI can take a long time
const EMPTY_FINDINGS: CIFinding[] = []
const EMPTY_CHECK_RUNS: CheckRunForAnalysis[] = []

/**
 * Computes analysis progress from findings and check-run metadata.
 * Used by both the refetchInterval callback and the progress memo to keep them in sync.
 */
export function computeAnalysisProgress(
  findings: CIFinding[],
  checkRunsForAnalysis: CheckRunForAnalysis[],
): AnalysisProgress {
  if (checkRunsForAnalysis.length === 0) {
    const analyzedCount = new Set(findings.map(f => f.failedCheckRunId)).size
    return {analyzedCount, failedCount: analyzedCount, pendingCount: 0}
  }

  const failedRuns = checkRunsForAnalysis.filter(cr => cr.status === 'failed')
  const pendingRuns = checkRunsForAnalysis.filter(cr => cr.status === 'in_progress' || cr.status === 'queued')
  const analyzedIds = new Set(findings.map(f => f.failedCheckRunId))
  const analyzedCount = failedRuns.filter(cr => analyzedIds.has(cr.id)).length
  return {
    analyzedCount,
    failedCount: failedRuns.length,
    pendingCount: pendingRuns.length,
  }
}

function mergeCIFindingsData(previous: CIFindingsData | undefined, current: CIFindingsData): CIFindingsData {
  const activeCheckRunIds =
    current.checkRunsForAnalysis === undefined
      ? undefined
      : new Set(current.checkRunsForAnalysis.map(checkRun => checkRun.id))

  if (activeCheckRunIds === undefined) {
    return current
  }

  // When all reruns passed (no failed runs in the active set), clear findings immediately.
  if (activeCheckRunIds.size === 0) {
    return {...current, findings: []}
  }

  const currentFindings = current.findings.filter(finding => activeCheckRunIds.has(finding.failedCheckRunId))

  if (!previous || previous.headSha !== current.headSha) {
    return {...current, findings: currentFindings}
  }

  // Keep previous findings visible while reruns are in progress so the UI doesn't
  // flash a loading state. Once the backend returns current findings (from active
  // check runs), those replace any stale findings from previous runs.
  const hasAnyCurrentFindings = currentFindings.length > 0

  // Retain previous findings unless they're from a stale check run that has already
  // been superseded by new findings from active runs.
  const retainedFindings = hasAnyCurrentFindings
    ? previous.findings.filter(finding => activeCheckRunIds.has(finding.failedCheckRunId))
    : previous.findings

  const findingsById = new Map(retainedFindings.map(finding => [finding.id, finding]))

  for (const finding of currentFindings) {
    findingsById.set(finding.id, finding)
  }

  return {...current, findings: [...findingsById.values()]}
}

export function useCIFindings({
  enabled = true,
  pollingTimeoutMs = MAX_POLL_DURATION_MS,
  apiUrl: apiUrlOverride,
}: {enabled?: boolean; pollingTimeoutMs?: number; apiUrl?: string} = {}): {
  findings: DedupedCIFinding[]
  headSha: string | undefined
  isPolling: boolean
  isAnalyzing: boolean
  isError: boolean
  progress: AnalysisProgress
} {
  const pageDataUrl = usePageDataUrl(PageData.ciFindings)
  const apiURL = apiUrlOverride ?? pageDataUrl
  const [pollingExpired, setPollingExpired] = useState(false)

  const {data, isError} = useQuery({
    queryKey: ['ciFindings', apiURL],
    queryFn: async (): Promise<CIFindingsData> => {
      const response = await fetchWithErrorHandling(apiURL)
      if (!response.ok && response.status >= 500) {
        throw new ServerUnavailableError(response.status)
      }
      const json = (await parseJSONWithBetterErrors(response)) as CIFindingsAPIResponse & {error?: string}
      throwErrorsIfBadResponse(response, json)
      return {
        findings: json.ciFindings ?? [],
        headSha: json.headSha,
        checkRunsForAnalysis: json.checkRunsForAnalysis,
      }
    },
    structuralSharing: (previous, current) =>
      mergeCIFindingsData(previous as CIFindingsData | undefined, current as CIFindingsData),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false
      return (
        error instanceof ServerUnavailableError ||
        error.name === 'FetchRequestError' ||
        error.name === 'JSONParseFetchError' ||
        error.name === 'TypeError'
      )
    },
    enabled,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    refetchInterval: query => {
      if (query.state.status === 'error') return false
      if (pollingExpired) return false
      if (!query.state.data) return POLL_INTERVAL_MS

      const {findings, checkRunsForAnalysis} = query.state.data

      if (checkRunsForAnalysis !== undefined) {
        // Keep watching for reruns on the same SHA after the current analysis completes.
        return POLL_INTERVAL_MS
      }

      // Fallback: no checkRunsForAnalysis in response (backend hasn't been updated yet)
      if (findings.length > 0) return false
      return POLL_INTERVAL_MS
    },
  })

  const rawFindings = data?.findings ?? EMPTY_FINDINGS
  const checkRunsForAnalysis = data?.checkRunsForAnalysis ?? EMPTY_CHECK_RUNS
  const hunkContainmentDedupEnabled = isFeatureEnabled('ci_analysis_hunk_containment_enabled')

  const shouldPoll = enabled && !isError

  // Reset pollingExpired when polling conditions clear (set-state-during-render)
  if (!shouldPoll && pollingExpired) {
    setPollingExpired(false)
  }

  useEffect(() => {
    if (!shouldPoll) return
    const timer = setTimeout(() => setPollingExpired(true), pollingTimeoutMs)
    return () => clearTimeout(timer)
  }, [shouldPoll, pollingTimeoutMs])

  useEffect(() => {
    if (isError) {
      sendStats({incrementKey: 'CI_FINDINGS_FETCH_ERROR'})
    }
  }, [isError])

  const progress: AnalysisProgress = useMemo(
    () => computeAnalysisProgress(rawFindings, checkRunsForAnalysis),
    [checkRunsForAnalysis, rawFindings],
  )

  const isPolling =
    shouldPoll &&
    !pollingExpired &&
    rawFindings.length === 0 &&
    (data?.checkRunsForAnalysis === undefined || data.checkRunsForAnalysis.length > 0)

  const isAnalyzing = useMemo(() => {
    if (!shouldPoll || pollingExpired || rawFindings.length === 0) return false
    if (data?.checkRunsForAnalysis === undefined) return false
    return progress.analyzedCount < progress.failedCount || progress.pendingCount > 0
  }, [shouldPoll, pollingExpired, rawFindings, data?.checkRunsForAnalysis, progress])

  const findings = useMemo(
    () => deduplicateCIFindings(rawFindings, hunkContainmentDedupEnabled),
    [hunkContainmentDedupEnabled, rawFindings],
  )

  return {
    findings,
    headSha: data?.headSha,
    isPolling,
    isAnalyzing,
    isError,
    progress,
  }
}
